import { useEffect, useState } from "react";
import { ChevronRight } from "lucide-react";
import PlanComparison from "./billing/PlanComparison";
import { LocationCTA } from "./billing/LocationCTA";

// Billing panel for SettingsScreen. Mirrors EmailSendingSettings: a default
// export taking { osId }, self-fetching its own data on mount. Reads the live
// usage from GET /api/billing/usage (plan, billing_status, used/cap), shows the
// shared PlanComparison for upgrades, and drives the Stripe portal (manage).

type Usage = {
  plan: string;
  billing_status: string | null;
  used: number;
  cap: number | null;
};

const STATUS_PILL: Record<string, string> = {
  active:   "bg-emerald-50 text-emerald-700 border-emerald-200",
  trialing: "bg-blue-50 text-blue-700 border-blue-200",
  past_due: "bg-amber-50 text-amber-700 border-amber-200",
  canceled: "bg-red-50 text-red-700 border-red-200",
};

function displayPlanName(plan: string): string {
  if (plan === "business") return "Custom Call";
  if (plan === "enterprise") return "Enterprise";
  if (plan === "pro") return "Pro";
  if (plan === "starter") return "Starter";
  return plan;
}

export default function BillingSettings({ osId }: { osId: string }) {
  const [usage, setUsage] = useState<Usage | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState("");
  // Plans stay collapsed by default — Settings should lead with the meter, not
  // a wall of pricing. The full comparison opens behind the "Change plan" toggle.
  const [showPlans, setShowPlans] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/billing/usage?workspace_id=${encodeURIComponent(osId)}`, { credentials: "include" });
        if (res.ok && !cancelled) { const j = await res.json(); setUsage(j); }
      } catch { /* leave empty; user can reload */ }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [osId]);

  async function openPortal() {
    setErr(""); setBusy("portal");
    try {
      const res = await fetch("/api/billing/portal", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ os_id: osId }),
      });
      const j = await res.json().catch(() => ({}));
      if (res.ok && j?.url) { window.location.href = j.url; return; }
      setErr(j?.error === "no_customer" ? "No billing account yet — pick a plan first." : (j?.error || "Could not open billing."));
    } catch { setErr("Could not open billing."); }
    setBusy(null);
  }

  if (loading) {
    return <p className="text-sm text-zinc-500">Loading billing…</p>;
  }

  const plan = usage?.plan || "trial";
  const cap = usage?.cap ?? null;
  const used = usage?.used ?? 0;
  const pct = cap && cap > 0 ? Math.min(100, Math.round((used / cap) * 100)) : 0;
  const status = usage?.billing_status || "";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-400">Billing</span>
        <LocationCTA />
      </div>
      {/* Calm, informational panel — the usage meter is the hero (what people
          open Settings to check). Plans stay collapsed behind "Change plan". */}
      <div className="rounded-xl border border-zinc-200 bg-white p-5 space-y-4">
        {/* Hero: plan + status on one line */}
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-base font-semibold text-zinc-900">{displayPlanName(plan)}</h3>
          {status ? (
            <span className={`px-2 py-0.5 text-xs rounded-full border capitalize ${STATUS_PILL[status] || "bg-zinc-50 text-zinc-600 border-zinc-200"}`}>
              {status.replace(/_/g, " ")}
            </span>
          ) : null}
        </div>

        {/* Usage meter — promoted as the prominent element */}
        <div>
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-xs font-medium uppercase tracking-wide text-zinc-500">Usage this period</span>
            <span className="text-sm text-zinc-900">
              <span className="font-semibold tabular-nums">{used}</span>
              {cap !== null ? (
                <span className="text-zinc-500"> / {cap} credits</span>
              ) : (
                <span className="text-zinc-500"> credits · unlimited</span>
              )}
            </span>
          </div>
          {cap !== null ? (
            <div className="h-2.5 w-full rounded-full bg-zinc-100 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${pct >= 100 ? "bg-red-500" : pct >= 80 ? "bg-amber-500" : "bg-emerald-500"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
          ) : (
            <div className="h-2.5 w-full rounded-full bg-emerald-100" />
          )}
        </div>

        {err ? <p className="text-sm text-red-600">{err}</p> : null}

        {/* Primary action + quiet plan-switch toggle */}
        <div className="flex items-center justify-between gap-3 pt-1">
          <button
            type="button"
            onClick={openPortal}
            disabled={busy === "portal"}
            className="px-4 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 text-white text-sm font-medium transition-colors disabled:opacity-60"
          >
            {busy === "portal" ? "Opening…" : "Manage billing"}
          </button>
          <button
            type="button"
            onClick={() => setShowPlans((v) => !v)}
            aria-expanded={showPlans}
            className="inline-flex items-center gap-0.5 text-sm font-medium text-zinc-500 hover:text-zinc-900 transition-colors"
          >
            {showPlans ? "Hide plans" : "Change plan"}
            <ChevronRight size={15} className={`transition-transform ${showPlans ? "rotate-90" : ""}`} />
          </button>
        </div>
      </div>

      {/* Collapsed by default — neutral-themed comparison (currentPlan set), so
          the current tier shows "Current plan" and there's no amber sales wall. */}
      {showPlans ? (
        <div className="pt-1">
          <PlanComparison osId={osId} currentPlan={plan} />
        </div>
      ) : null}
    </div>
  );
}
